import {
  Box,
  Grid,
  TextField,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  Button,
  Paper,
  Divider,
} from "@mui/material";
import { useState, ChangeEvent, FormEvent, useCallback } from "react";
import { createLead } from "../../services/leadsService";
import SuccessfulRegistration from "../../components/Modal/SuccessfulRegistration";

interface LeadForm {
  business_name: string;
  business_type: string;
  contact_name: string;
  position: string;
  phone: string;
  email: string;
  instagram: string;
  website: string;
  street: string;
  city: string;
  province: string;
  postal_code: string;
  products: string[];
  monthly_volume: string;
  has_supplier: string;
  start_timeline: string;
  decision_maker: string;
  follow_up_method: string;
  best_time: string;
  lead_quality: string;
  notes: string;
  supplier: string;
  contact_method: string;
  order_date: string;
}

const initialState: LeadForm = {
  business_name: "",
  business_type: "",
  contact_name: "",
  position: "",
  phone: "",
  email: "",
  instagram: "",
  website: "",
  street: "",
  city: "",
  province: "",
  postal_code: "",
  products: [],
  monthly_volume: "",
  has_supplier: "",
  start_timeline: "",
  decision_maker: "",
  follow_up_method: "",
  best_time: "",
  lead_quality: "",
  notes: "",
  supplier: "",
  contact_method: "",
  order_date: "",
};

export default function TradeShowLeadForm() {
  const [form, setForm] = useState<LeadForm>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successModal, setSuccessModal] = useState(false);
  const [otherBusiness, setOtherBusiness] = useState("");
  // const handleChange = useCallback(
  //   ({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  //     const { name, value } = target;
  //     setForm((prev) => ({ ...prev, [name]: value }));
  //   },
  //   [],
  // );
  const handleChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = target;

      setForm((prev) => ({ ...prev, [name]: value }));

      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    },
    [],
  );

  const handleProductChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const { value, checked } = target;

      setForm((prev) => ({
        ...prev,
        products: checked
          ? [...prev.products, value]
          : prev.products.filter((p) => p !== value),
      }));
    },
    [],
  );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const mandatoryFields = [
      "business_name",
      "business_type",
      "contact_name",
      "phone",
      "email",
      "street",
      "city",
      "province",
      "postal_code",
    ];
    if (mandatoryFields.some((field) => !form[field as keyof LeadForm])) {
      alert(`Please fill in all mandatory fields. `);
      return;
    }
    const newErrors: Record<string, string> = {};

    mandatoryFields.forEach((field) => {
      if (!form[field as keyof LeadForm]) {
        newErrors[field] = "This field is required";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({}); // clear errors if valid
    //   const data = { ...form }; // copy current form state
    const data = {
      ...form,
      business_type:
        form.business_type === "Other" ? otherBusiness : form.business_type,
      products: Array.isArray(form.products) ? form.products : [],
    };

    try {
      // call your API service
      const result = await createLead({ data });
      if (!result) throw new Error("Business already registered.");
      // reset form only after successful submission
      setForm(initialState);
      setSuccessModal(true);
      // alert("Lead Saved ✅");
    } catch (err: any) {
      console.error("Failed to save lead:", err);
      alert(err.message || "Failed to save lead ❌");
    }
  };
  return (
    <Box p={3}>
      <SuccessfulRegistration
        success={true}
        title="Registration successful"
        message="Congratulations! You have successfully registered."
        setOpen={setSuccessModal}
        open={successModal}
      />
      <Paper
        elevation={3}
        sx={{
          p: 4,
          height: "calc(100vh - 22vh)",
          overflow: "scroll",
          scrollbarWidth: "none",
        }}
      >
        <Typography variant="h4" gutterBottom>
          Asadi Inc – Wholesale Inquiry
        </Typography>

        <form onSubmit={handleSubmit}>
          <Typography variant="h6">1. Business Information</Typography>
          <Grid container spacing={2} mt={1}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Business Name"
                name="business_name"
                error={!!errors.business_name}
                value={form.business_name}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl error={!!errors.business_type}>
                <FormLabel>Type of business</FormLabel>
                <RadioGroup
                  row
                  name="business_type"
                  value={form.business_type}
                  onChange={handleChange}
                >
                  {[
                    "Restaurant",
                    "Café",
                    "Bar / Brewery",
                    "Grocery Store",
                    "Distributor",
                    "Hotel",
                    "Catering",
                    "Other",
                  ].map((type) => (
                    <FormControlLabel
                      key={type}
                      value={type}
                      control={<Radio />}
                      label={type}
                    />
                  ))}
                </RadioGroup>
                {form.business_type === "Other" && (
                  <TextField
                    label="Enter business type"
                    value={otherBusiness}
                    onChange={(e) => {
                      const value = e.target.value;
                      setOtherBusiness(value);
                    }}
                    fullWidth
                    margin="normal"
                  />
                )}
              </FormControl>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3 }} />
          <Grid container spacing={2} mt={1}>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Contact Name"
                name="contact_name"
                error={!!errors.contact_name}
                value={form.contact_name}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Position"
                name="position"
                error={!!errors.position}
                value={form.position}
                onChange={handleChange}
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                error={!!errors.phone}
                value={form.phone}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                error={!!errors.email}
                value={form.email}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormLabel>Business address</FormLabel>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Street"
                name="street"
                error={!!errors.street}
                value={form.street}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="City"
                name="city"
                error={!!errors.city}
                value={form.city}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <TextField
                fullWidth
                label="Province"
                name="province"
                error={!!errors.province}
                value={form.province}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <TextField
                fullWidth
                label="Postal Code"
                name="postal_code"
                error={!!errors.postal_code}
                value={form.postal_code}
                onChange={handleChange}
                required
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 4 }} />
          {/* <Typography variant="h6">2. Product Interest</Typography>

          <Grid container spacing={1}>
            {[
              "Roasted Nuts",
              "Flavoured Nuts",
              "Caramelized Nuts",
              "Dried Fruits",
              "Flavoured Chocolate",
              "Flavoured Nougat",
              "Olives",
              "Turkish Delights",
            ].map((product) => (
              <Grid key={product} size={{ xs: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      value={product}
                      checked={form.products.includes(product)}
                    //   onChange={(e) => handleCheckboxArray(e, "products")}
                    />
                  }
                  label={product}
                />
              </Grid>
            ))}
          </Grid> */}
          <Typography variant="h6">Product Interest</Typography>
          <Grid container>
            {[
              "Roasted Nuts",
              "Flavoured Nuts",
              "Caramelized Nuts",
              "Dried Fruits",
              "Flavoured Chocolate",
              "Flavoured Nougat",
              "Olives",
              "Turkish Delights",
            ].map((product) => (
              <Grid size={{ xs: 6 }} key={product}>
                <FormControlLabel
                  control={
                    <Checkbox
                      value={product}
                      checked={form.products.includes(product)}
                      onChange={handleProductChange}
                    />
                  }
                  label={product}
                />
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ my: 3 }} />
          <Grid size={{ xs: 12 }}>
            <FormControl error={!!errors.monthly_volume}>
              <FormLabel>Estimated Monthly Order Volume</FormLabel>
              <RadioGroup
                row
                name="monthly_volume"
                value={form.monthly_volume}
                onChange={handleChange}
              >
                {[
                  "Under $500",
                  "$500 - $2,000",
                  "$2,000 - $5,000",
                  "$5,000+",
                ].map((type) => (
                  <FormControlLabel
                    key={type}
                    value={type}
                    control={<Radio />}
                    label={type}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
          <Divider sx={{ my: 3 }} />
          <Grid container>
            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <FormLabel>Are you currently working with a supplier?</FormLabel>
              <RadioGroup
                row
                name="supplier"
                value={form.supplier}
                onChange={handleChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <FormLabel>When are you looking to start ordering?</FormLabel>
              <RadioGroup
                row
                name="order_date"
                value={form.order_date}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="Immediately"
                  control={<Radio />}
                  label="Immediately"
                />
                <FormControlLabel
                  value="Within 1–3 months"
                  control={<Radio />}
                  label="Within 1–3 months"
                />
                <FormControlLabel
                  value="Just exploring"
                  control={<Radio />}
                  label="Just exploring"
                />
              </RadioGroup>
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <FormLabel>Decision Maker?</FormLabel>
              <RadioGroup
                row
                name="decision_maker"
                value={form.decision_maker}
                onChange={handleChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 3 }} />
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <FormLabel>Preferred Contact Method:</FormLabel>
              <RadioGroup
                row
                name="contact_method"
                value={form.contact_method}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="Email"
                  control={<Radio />}
                  label="Email"
                />
                <FormControlLabel
                  value="Phone"
                  control={<Radio />}
                  label="Phone"
                />
                <FormControlLabel
                  value="WhatsApp"
                  control={<Radio />}
                  label="WhatsApp"
                />
              </RadioGroup>
            </Grid>
            {/* <Grid
              size={{ xs: 12, md: 4, lg: 4 }}
              sx={{ gap: 4, display: "flex" }}
            >
              <FormLabel sx={{ marginTop: 1 }}>Lead Type:</FormLabel>
              <TextField
                name="leadType"
                size="small"
                // value={form.leadType}
                onChange={handleChange}
              />
            </Grid> */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 3 }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl error={!!errors.notes} sx={{ width:'70vw'}}>
                <TextField
                  name="notes"
                  size="small"
                  label="Notes"
                  value={form.notes}
                  sx={{ flexGrow: 1 }}
                  fullWidth
                  onChange={handleChange}
                />
              </FormControl>
            </Grid>
            {/* <Grid size={{ xs: 12 }}>
              <FormLabel>Internal Lead Quality</FormLabel>
              <RadioGroup
                row
                name="lead_quality"
                value={form.lead_quality}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="Hot"
                  control={<Radio color="error" />}
                  label="Hot 🔥"
                />
                <FormControlLabel
                  value="Warm"
                  control={<Radio color="warning" />}
                  label="Warm 🌡 ️"
                />
                <FormControlLabel
                  value="Cold"
                  control={<Radio />}
                  label="Cold 🥶"
                />
              </RadioGroup>
            </Grid> */}
          </Grid>
          <Box mt={4}>
            <Button type="submit" variant="contained" size="large" fullWidth>
              Save
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
